import React, { useState, useEffect, useContext } from "react";
import _ from "lodash";
import {
	BgColorToTextColorMap,
	BgColorToTextColorMapFunc,
	CodesToColorMapFunc,
} from "../../CodesToColorMap";
import { BackButton } from "../common/BackButton";
import { ModalDialog } from "../common/ModalDialog";
import { NextButton } from "../common/NextButton";
import { Select } from "../common/Select";
import {
	deleteCost,
	getCostTypes,
	saveCost,
} from "../../services/dashApiService";
import {
	getDaysInMonth,
	format,
	subMonths,
	getDate,
	getTime,
	startOfDay,
	setDate,
} from "date-fns";
import NumberFormat from "react-number-format";
import { maxCostAmount } from "../../configs/costsConfig";
import { LoadingSpinnerAlt } from "../common/LoadingSpinnerAlt";
import { sanitize } from "dompurify";
import he from "he";
import { DiseaseGroupContext } from "../../contexts/diseaseGroupContext";

export const CostPopup = ({
	show,
	onClose,
	onCostSave,
	costItem,
	newCostMonth,
}) => {
	const { theme } = useContext(DiseaseGroupContext);
	const themeCost = theme.cost();

	const bgClassReadOnly = "bg-white bg-opacity-30";

	const isLocked = costItem ? costItem.locked : false;
	const canDelete = costItem && !isLocked;

	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);

	const getInitialDate = () => {
		const d = costItem
			? costItem.incurred
			: newCostMonth === "lastMonth"
			? subMonths(startOfDay(Date.now()), 1)
			: startOfDay(Date.now());
		return d;
	};

	const [currentCostDate, setCurrentCostDate] = useState();
	const [dateDisplay, setDateDisplay] = useState();
	const [dayOptions, setDayOptions] = useState([]);
	useEffect(() => {
		if (show) {
			const d = getInitialDate();

			setCurrentCostDate(d);
			setDateDisplay(format(d, "LLL yyyy"));

			const days = _.range(1, getDaysInMonth(d) + 1);

			setDayOptions(
				days.map((d) => {
					return {
						id: d,
						label: d,
						value: d,
					};
				})
			);
		}
	}, [show]);

	const [currentDateOption, setCurrentDateOption] = useState({
		id: 1,
		value: 1,
		label: 1,
	});
	useEffect(() => {
		if (dayOptions.length > 0) {
			setCurrentDateOption(dayOptions[getDate(currentCostDate) - 1]);
		}
	}, [dayOptions]);
	useEffect(() => {
		if (currentCostDate) {
			const date = setDate(currentCostDate, currentDateOption.value);
			setCurrentCostDate(date);
		}
	}, [currentDateOption]);

	const costTypePlaceholder = {
		id: "none",
		value: "none",
		label: "-- No option selected --",
	};

	const [costTypes, setCostTypes] = useState([costTypePlaceholder]);

	useEffect(() => {
		let didCancel = false;

		if (show) {
			setIsLoading(true);
			setHasError(false);

			getCostTypes()
				.then((result) => {
					if (!didCancel) {
						if (result.success) {
							const options = result.data.list.map((o) => {
								let p = {};
								p.id = o.value;
								p.value = o.value;
								p.label = o.display;
								return p;
							});
							options.unshift(costTypePlaceholder);
							setCostTypes(options);
						} else {
							if (result.err) {
								console.error(
									`Dash API error: ${result.err.code}: ${result.err.msg}`
								);
							} else {
								console.error(
									`Unknown error occurred in CostPopup. API response: ${result}`
								);
							}
							setHasError(true);
						}
					}
				})
				.catch((error) => {
					console.error("Error in CostPopup: ", error);
					setHasError(true);
				});

			if (costItem) {
				setCurrentAmount(costItem.amount);
				setCurrentDescription(he.decode(costItem.description));
			}

			setIsLoading(false);
		}

		return () => {
			didCancel = true;
		};
	}, [show]);

	useEffect(() => {
		if (costTypes.length > 1) {
			if (costItem) {
				setCurrentCostType(costTypes.find((type) => type.id === costItem.type));
			}
		}
	}, [costTypes]);

	const [currentCostType, setCurrentCostType] = useState(costTypes[0]);
	const [currentAmount, setCurrentAmount] = useState();

	const handleCurrentAmountChange = (values) => {
		if (values.floatValue) {
			setCurrentAmount(values.floatValue);
		} else {
			setCurrentAmount(undefined);
		}
	};

	const [currentDescription, setCurrentDescription] = useState("");
	const handleDescriptionChange = (event) => {
		const clean = sanitize(event.target.value);
		const unescaped = he.decode(clean);
		setCurrentDescription(unescaped);
	};

	const [costData, setCostData] = useState({});
	useEffect(() => {
		setCostData({
			id: costData.id,
			incurred: getTime(currentCostDate),
			description: currentDescription,
			type: currentCostType.value,
			amount: currentAmount,
		});
	}, [currentAmount, currentCostDate, currentCostType, currentDescription]);
	useEffect(() => {
		if (show) {
			if (costItem) {
				setCostData({ id: costItem.id });
			} else {
				setCostData({ id: null });
			}
		}
	}, [show]);

	const [isSaveDisabled, setIsSaveDisabled] = useState(true);
	useEffect(() => {
		const canSave =
			costData.amount && costData.incurred && costData.type !== "none";
		setIsSaveDisabled(!canSave);
	}, [costData]);

	const resetFormFields = () => {
		setCurrentAmount();
		setCurrentCostDate();
		setCurrentCostType(costTypes[0]);
		setCurrentDescription("");
	};

	const handleClose = () => {
		resetFormFields();
		setIsShowingDeleteConfirmation(false);
		onClose();
	};

	const handleSave = () => {
		setIsLoading(true);

		saveCost(costData)
			.then((result) => {
				if (result.success) {
					setIsLoading(false);
					onCostSave();
					handleClose();
				} else {
					if (result.err) {
						console.error(
							`Dash API error: ${result.err.code}: ${result.err.msg}`
						);
					} else {
						console.error(
							`Unknown error occurred in CostPopup. API response: ${result}`
						);
					}
					setIsLoading(false);
					setHasError(true);
				}
			})
			.catch((error) => {
				console.error("Error in CostPopup: ", error);
				setIsLoading(false);
				setHasError(true);
			});
	};

	const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] =
		useState(false);
	const handleDelete = () => {
		setIsShowingDeleteConfirmation(true);
	};

	const handleDeleteConfirmed = () => {
		setIsLoading(true);

		deleteCost(costData.id)
			.then((result) => {
				if (result.success) {
					setIsLoading(false);
					onCostDelete();
					handleClose();
				} else {
					if (result.err) {
						console.error(
							`Dash API error: ${result.err.code}: ${result.err.msg}`
						);
					} else {
						console.error(
							`Unknown error occurred in CostPopup. API response: ${result}`
						);
					}
					setIsLoading(false);
					setHasError(true);
				}
			})
			.catch((error) => {
				console.error("Error in CostPopup: ", error);
				setIsLoading(false);
				setHasError(true);
			});
	};

	const onCostDelete = () => onCostSave();

	const isValidAmount = (input) => {
		const { value } = input;
		if (value <= maxCostAmount) return true;
		return false;
	};

	const currentTheme = CodesToColorMapFunc("cost", theme);

	return (
		<ModalDialog show={show} onClose={handleClose}>
			<div
				className={
					"z-20 flex items-center rounded-sm justify-center text-black max-w-sm"
				}
			>
				<div
					className={`flex self-stretch rounded-sm mr-0.5 w-4 ${currentTheme.bg()}`}
				></div>
				<div className="bg-white">
					<div
						className={`${themeCost.bg()} bg-opacity-30 w-full h-full p-6 shadow-md`}
					>
						{isLoading ? (
							<div className="my-6">
								<LoadingSpinnerAlt color={"black"} />
							</div>
						) : hasError ? (
							<>
								<p className="my-3 xs:my-10 text-center mx-2">
									It has not been possible to retrieve your report.
								</p>
								<p className="my-3 xs:my-10 text-center mx-2">
									This is most likely because the connection to the internet has
									been lost.
								</p>
								<p className="my-3 xs:my-10 text-center mx-2">
									If this problem persists, please contact our helpdesk, quoting
									reference '#CP001'.
								</p>
							</>
						) : (
							<>
								{isShowingDeleteConfirmation ? (
									<>
										<h5 className={"text-xl font-bold block mb-6"}>
											DELETE COST
										</h5>
										<p className="my-6 xs:my-10 text-center mx-2">
											Are you sure you want to delete this cost item?
										</p>
									</>
								) : (
									<>
										<h5 className={"text-xl font-bold block mb-6"}>
											{isLocked ? "View" : costItem ? "Edit" : "Add new"} cost
										</h5>
										<div className="text-left">
											<div className={"mb-3 flex text-sm"}>
												<label
													className={"block pr-2 text-right w-32 my-auto"}
													htmlFor="amount"
												>
													Cost <span className={`text-opacity-50`}>£</span>
												</label>
												<div className={"w-full"}>
													<NumberFormat
														value={currentAmount}
														thousandSeparator={true}
														className={`formField-cost border-${
															theme.group
														}-cost block ${isLocked ? bgClassReadOnly : null}`}
														inputMode={"decimal"}
														decimalScale={2}
														fixedDecimalScale={true}
														onValueChange={(values) => {
															handleCurrentAmountChange(values);
														}}
														displayType={isLocked ? "text" : null}
														isAllowed={isValidAmount}
														allowNegative={false}
													/>
												</div>
											</div>

											<div className={"mb-4 flex text-sm"}>
												<label
													className={"block pr-2 text-right w-32 my-auto"}
													htmlFor="day"
												>
													Date of cost
												</label>
												<div className={"flex w-full space-x-2"}>
													<div className={"w-1/3"}>
														{dayOptions.length > 0 && currentDateOption ? (
															<Select
																containerStyles={`formField-cost border-${theme.group}-cost`}
																options={dayOptions}
																selectedOption={currentDateOption}
																handleChange={(value) => {
																	setCurrentDateOption(
																		dayOptions.find((day) => day.id === value)
																	);
																}}
																isReadOnly={isLocked}
															/>
														) : null}
													</div>
													<span
														className={`w-2/3 flex justify-center items-center text-white formField-cost border-${
															theme.group
														}-cost font-bold ${themeCost.bg()}`}
													>
														{dateDisplay}
													</span>
												</div>
											</div>

											<div className={"mb-3 text-sm"}>
												<label
													className={"pb-1 inline-block"}
													htmlFor="costType"
												>
													What type of cost is this:
												</label>
												{costTypes.length > 0 && currentCostType ? (
													<Select
														containerStyles={`formField-cost border-${theme.group}-cost`}
														options={costTypes}
														selectedOption={currentCostType}
														handleChange={(value) => {
															setCurrentCostType(
																costTypes.find((type) => type.id === value)
															);
														}}
														isReadOnly={isLocked}
													/>
												) : null}
											</div>

											<div className={"mb-9 text-sm"}>
												<label
													className={"pb-1 inline-block"}
													htmlFor="description"
												>
													Description (optional):
												</label>
												<textarea
													rows={3}
													type="text"
													className={`formField-cost border-${
														theme.group
													}-cost ${
														isLocked ? bgClassReadOnly : ""
													} leading-4 py-2`}
													value={currentDescription}
													onChange={handleDescriptionChange}
													disabled={isLocked}
													maxLength={255}
												/>
											</div>
										</div>
									</>
								)}
							</>
						)}

						<div className="flex justify-around">
							{isShowingDeleteConfirmation ? (
								<>
									<div className="w-32">
										<BackButton
											onClick={() => setIsShowingDeleteConfirmation(false)}
											textOverride={"Cancel"}
											variant="inactive"
											showIcon={false}
										/>
									</div>
									<div className="w-32">
										<NextButton
											onClick={handleDeleteConfirmed}
											textOverride={"Yes"}
											variant="active"
										/>
									</div>
								</>
							) : (
								<div className="flex w-full gap-2">
									<div className="flex-1">
										<BackButton
											onClick={handleClose}
											textOverride={isLocked ? null : "Cancel"}
											variant="inactive"
											showIcon={false}
										/>
									</div>
									{canDelete ? (
										<div className="flex-1">
											<NextButton
												onClick={handleDelete}
												textOverride={"Delete"}
												variant="active"
											/>
										</div>
									) : null}
									{isLocked ? null : (
										<div className="flex-1">
											<NextButton
												onClick={handleSave}
												textOverride={"Save"}
												disabled={isSaveDisabled || isLoading}
												variant="active"
											/>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</ModalDialog>
	);
};
